import _ from 'lodash';
import {
  FeatureAppTrackingManager,
  getDefaultTrackingEvent,
} from '../hooks/use-tracking-manager';
import {FormConfiguration} from '../typing/forms';
import {UsedVehicle} from '../typing/used-inventory';
import {DEFAULT_FUEL_TYPE} from './app-constants';

interface FormErrorFields {
  formFields?: string[];
  errorFields: string[];
  errorField?: string;
  errorMessage?: string;
}

const COUNTRY_CODE = "US";

const fieldsErrorCodes = {
  'Invalid email': 'FE01',
  'Field required': 'FE02',
  'Invalid phone number': 'FE03',
  'Invalid last name': 'FE04',
  'Last name must be at least 1 characters': 'FE05',
  'Invalid first name': 'FE06',
  'First name must be at least 1 characters': 'FE07',
  'Message must be at least 1 characters': 'FE08',
  'Message must be at most 1000 characters': 'FE09',
  'Please select valid model': 'FE10',
  'Please select valid trim': 'FE11',
  'Invalid Zipcode': 'FE12',
  '': 'FE00',
};

const formTypeMap = {
  'contact': 'contact',
  'quote': 'quote',
  'sign': 'signup',
  'newLetter': 'newsletter',
  '': 'otherform',
};

const dealerFormEvents = {
  load: 'VWBasic_DealerFormCTA_Click',
  success: 'VWBasic_DealerFormSubmissionSuccessMessage_Load',
  error: 'VWBasic_DealerFormFormErrorMessage_Load',
};

const offerFormEvents = {
  load: 'VWBasic_UsedVehicleFormCTA_Click',
  success: 'VWBasic_UsedVehicleFormSubmissionSuccessMessage_Load',
  error: 'VWBasic_UsedVehicleFormErrorMessage_Load',
};

export const onVWBasicLayerCloseClick = (
  trackingManager?: FeatureAppTrackingManager,
  vehicle?: UsedVehicle,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.user = {
    loginStatus: false,
  };

  if (!_.isEmpty(_.omit(vehicle, 'dealer'))) {
    customEvent.configuration = {
      CarlineId: undefined,
      CarlineName: vehicle?.model,
      SalesgroupId: undefined,
      SalesgroupName: vehicle?.trimLevel || undefined,
      EquipmentlineName: vehicle?.trimLevel,
      DataSourceIsVicci: false,
    };

    customEvent.link = {
      url: 'Close Modal',
      name: 'Close Modal',
    };
  }

  if (vehicle?.dealer) {
    customEvent.partner = {
      informationBnr: vehicle.dealer.dealerid,
      informationName: vehicle.dealer.name,
      informationZIP: vehicle.dealer.postalcode,
      informationDistance: vehicle.dealer.distance,
    };
  }

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_LayerClose_Click',
      customEvent,
    );
};

/**
 * handle zipcode change tracking
 * @param name
 * @param trackingManager
 * @param store
 */
export const onVWUsedVehicleFormLayerLoad = (
  trackingManager?: FeatureAppTrackingManager,
  model?: FormConfiguration,
  vehicle?: UsedVehicle,
  isDealerForm?: boolean,
) => {
  if (model?.disableTracking) return;
  let customEvent = getDefaultTrackingEvent();
  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `${model?.formName} form`,
  };
  customEvent.form = getBasicFormData(
    model?.formName,
    customEvent.form,
    true,
    model?.formTrackingType,
  );
  if (vehicle) {
    customEvent.configuration = {
      ...customEvent.configuration,
      Manufacturer: 'Volkswagen',
      BodyTypeName: undefined,
      CarlineId: vehicle?.modelCodeKey,
      CarlineName: vehicle?.model,
      SalesgroupId: vehicle?.modelCodeKey,
      SalesgroupName: vehicle?.trimLevel,
      EquipmentlineName: vehicle?.trimLevel,
      ModelId: vehicle?.modelCode,
      ModelName: vehicle?.model,
      ModelYear: vehicle?.modelYear,
      ModelDriveType: vehicle?.engineDescription,
      ModelGearType: vehicle?.transmissionDescription,
      EnginePowerHPKW: undefined,
      EngineFuelTypePrimary:
        DEFAULT_FUEL_TYPE?.typeMap[
          vehicle?.engineFuelType ? vehicle?.engineFuelType : ''
        ],
      EmissionCo2CombinedPrimary: undefined,
      ConsumptionCombinedPrimary: undefined,
      EmissionConsumptionEfficiencyClass: undefined,
      ExteriorColorCode: vehicle?.factoryExteriorCode,
      ExteriorColorName: vehicle?.exteriorColorDescription,
      InteriorColorCode: vehicle?.factoryInteriorCode,
      InteriorColorName: vehicle?.interiorColorDescription,
      PriceType: undefined,
      PriceTypeParameter: undefined,
      PriceRateCurrency: 'USD',
      PriceRateType: undefined,
      PriceRateBasic: vehicle?.adjustedListPrice,
      PriceRateAdditional: undefined,
      PriceRateTotal: undefined,
      PriceTotalCurrency: 'USD',
      PriceTotalBasic: undefined,
      PriceTotalAdditional: undefined,
      PriceTotalTotal: undefined,
      UsedVehicle: vehicle?.usedType === 'Y',
      UsedVehicleCondition: undefined,
      UsedVehicleRegistrationDate: undefined,
      UsedVehicleMileage: vehicle?.odometer,
      UsedVehicleComissionNumber: undefined,
    };
  }
  if (vehicle?.dealer) {
    customEvent.partner = {
      informationBnr: vehicle.dealer.dealerid,
      informationName: vehicle.dealer.name,
      informationZIP: vehicle.dealer.postalcode,
      informationDistance: vehicle.dealer.distance,
    };
  }
  trackingManager &&
    trackingManager.trackFormsGlobalEvent(
      isDealerForm
        ? 'VWBasic_DealerFormLayer_Load'
        : 'VWBasic_UsedVehicleFormLayer_Load',
      customEvent,
    );
};

/**
 * Vehicle button form tag
 * @param offer
 * @param name
 * @param trackingManager
 * @param store
 */
export const onHandleBasicFormButtonLink = (
  trackingManager: FeatureAppTrackingManager,
  formConfig?: FormConfiguration,
  vehicle?: UsedVehicle,
) => {
  if (formConfig?.disableTracking) return;
  const formEvent = formConfig?.isDealerForm
    ? 'VWBasic_DealerButton_Click'
    : 'VWBasic_DealerOfferCTA_Click';
  let customEvent = getDefaultTrackingEvent();

  if (vehicle)
    customEvent.configuration = {
      ...customEvent.configuration,
      BodyTypeName: undefined,
      CarlineId: vehicle?.modelCodeKey,
      CarlineName: vehicle?.model,
      SalesgroupId: vehicle?.modelCodeKey,
      SalesgroupName: vehicle?.trimLevel,
      EquipmentlineName: vehicle?.trimLevel,
      ModelId: vehicle?.modelCode,
      ModelName: vehicle?.model,
      ModelYear: vehicle?.modelYear,
      PriceType: undefined,
      PriceTypeParameter: undefined,
      PriceRateCurrency: 'USD',
      PriceRateType: undefined,
      PriceRateBasic: vehicle?.adjustedListPrice,
      PriceRateAdditional: undefined,
      PriceRateTotal: undefined,
      PriceTotalCurrency: 'USD',
      PriceTotalBasic: undefined,
      PriceTotalAdditional: undefined,
      PriceTotalTotal: undefined,
      UsedVehicle: vehicle?.usedType === 'Y',
      UsedVehicleCondition: undefined,
    };
  customEvent.eventInfo = getContentInfo(
    false,
    formConfig?.formName,
    customEvent.eventInfo,
  );
  customEvent.link = {
    ...customEvent.link,
    url: `open ${formConfig?.formName} form modal`,
    name: formConfig?.formName,
    newTab: false,
  };
  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
  };

  customEvent.partner = {
    ...customEvent.partner,
    informationBnr: vehicle?.dealer?.dealerid,
    informationName: vehicle?.dealer?.name,
    informationZIP: vehicle?.dealer?.postalcode,
    informationDistance: vehicle?.dealer?.distance,
  };

  trackingManager.trackFormsGlobalEvent(formEvent, customEvent);
};

/**
 * Handle form status tracking
 * @param type
 * @param trackingManager
 * @param offer
 * @param formValues
 * @param formId
 * @param error
 */
export const onHandleFormSubmitTracking = (
  type: 'load' | 'success' | 'error',
  trackingManager?: FeatureAppTrackingManager,
  formConfig?: FormConfiguration,
  formValues?: any,
  formId?: string,
  error?: FormErrorFields,
  errorMessage?: {message?: string; code?: string},
  vehicle?: UsedVehicle,
) => {
  if (formConfig?.disableTracking) return;
  let customEvent = getDefaultTrackingEvent();
  const trackingType = formConfig?.isDealerForm
    ? dealerFormEvents
    : offerFormEvents;
  const event = trackingType[type];
  if (type === 'load')
    customEvent.link = {
      ...customEvent.link,
      url: 'Submit form',
      name: `Submit`,
    };
  customEvent.form = getBasicFormData(
    formConfig?.formName,
    customEvent.form,
    false,
    formConfig?.formTrackingType,
  );
  if (type !== 'error') {
    customEvent.form = {
      ...customEvent.form,
      PrefContactChannels: formValues?.preferredContactMethod,
      LeadID: createSuccessLeadId(formId,COUNTRY_CODE),
      FormStart: false,
      MarketingConsent: undefined,
    };
    if (vehicle || formValues) {
      customEvent.configuration = {
        ...customEvent.configuration,
        CarlineId: vehicle?.modelCodeKey,
        CarlineName: vehicle?.model || formValues?.modelName,
        SalesgroupId: vehicle?.modelCodeKey,
        SalesgroupName: formValues
          ? formValues?.modelName + ' ' + formValues?.trim
          : undefined,
        EquipmentlineName: vehicle?.trimLevel,
        UsedVehicle: vehicle?.usedType === 'Y',
      };

      customEvent.partner = {
        informationBnr: vehicle?.dealer?.dealerid,
        informationName: vehicle?.dealer?.name,
        informationZIP: vehicle?.dealer?.postalcode,
        informationDistance: vehicle?.dealer?.distance,
      };
    }
  }
  if (type === 'success') {
    customEvent.form = getFormFields(formValues, customEvent.form);
  }
  if (error) {
    customEvent.form = getErrorFields(error, customEvent.form);
  }
  if (errorMessage) {
    customEvent.error = {
      ...customEvent.error,
      code: errorMessage.code || fieldsErrorCodes[errorMessage.message || ''],
      message: errorMessage.message,
    };
  }
  customEvent.eventInfo = getContentInfo(
    false,
    formConfig?.formName,
    customEvent.eventInfo,
  );
  trackingManager && trackingManager.trackFormsGlobalEvent(event, customEvent);
};

function getFormType(formName?: string, isNewsLetter?: boolean) {
  if (isNewsLetter) return formTypeMap['newLetter'];
  if (!formName) return formTypeMap[''];
  const formType = formName.toLocaleLowerCase();
  if (formType.includes('contact')) return formTypeMap['contact'];
  if (formType.includes('sign')) return formTypeMap['sign'];
  if (formType.includes('quote')) return formTypeMap['quote'];
  return formTypeMap[''];
}

function getBasicFormData(
  formName?: string,
  eventObject?: any,
  isFormStart?: boolean,
  formType?: string,
) {
  return {
    ...eventObject,
    FormType: formType || getFormType(formName || '', false),
    FormName: formName,
    FormStart: isFormStart ? true : undefined,
    FormVersion: '1',
    NewsletterType: false,
  };
}

function getFormFields(formFieldsEvent: any, event: any) {
  const formFieldsSchema = {
    firstName: 'firstName',
    lastName: 'lastName',
    email: 'email',
    phone: 'phone',
    preferredContactMethod: 'email',
    comments: 'comments',
    modelName: 'modelName',
    trim: 'trim',
    zip: 'zip',
  };
  const cleanedFields = Object.keys(formFieldsEvent).filter(
    (formField) => formFieldsSchema[formField],
  );
  const formFields: any[] = [];
  cleanedFields?.map((key: string) => {
    formFields.push({formfieldname: key});
  });
  return {...event, FormFields: formFields};
}

function getErrorFields(error: FormErrorFields, event: any) {
  const errorFields: any[] = [];
  const formFields: any[] = [];
  error.errorFields.map((key: string) => {
    errorFields.push({formfieldname: key});
  });
  error.formFields?.map((key: string) => {
    formFields.push({formfieldname: key});
  });
  return {
    ...event,
    ErrorFormFields: errorFields,
    FormFields: formFields,
    FormFieldName: error.errorField,
  };
}

const getContentInfo = (isInline?: boolean, formName?: string, event?: any) => {
  if (!isInline) {
    return {...event, contentId: 'Form Layer'};
  } else {
    return {
      ...event,
      contentId: `${formName} form`,
    };
  }
};

/**
 * Create Success request response according with configured data
 * @param formId request form response
 * @param successRequestCountryCode : Configured Tracking Variable
 * @param successRequestLeadId : Configured Tracking Variable
 * @returns new request Id | current request response | undefined
 */
 function createSuccessLeadId(formId?: string | undefined, successRequestCountryCode?: string | undefined, successRequestLeadId?: string | undefined): string | undefined {
  if (!formId) return undefined;
  let newRequestId = formId;
  if (successRequestLeadId) {
    newRequestId= `${successRequestLeadId}-${newRequestId}`;
  }
  if (successRequestCountryCode) {
    newRequestId= `${successRequestCountryCode}-${newRequestId}`;
  }
  return newRequestId;
}